import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFullDate, formatDate, capitalize } from '../../util';

/* MUI components */
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Fab from '@mui/material/Fab';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

/* MUI icons */
import PostAddIcon from '@mui/icons-material/PostAdd';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import axios from 'axios';
import { LineChart } from '@mui/x-charts';

/* Local components & utils */
import AddClientModal from '../../components/Modals/AddClientModal/AddClientModal';
import AddMeasureModal from '../../components/Modals/AddMeasureModal/AddMeasureModal';
import AddExamModal from '../../components/Modals/AddExamModal/AddExamModal';
import GenerateDietModal from '../../components/Modals/GenerateDietModal/GenerateDietModal';
import EditDietModal from '../../components/Modals/EditDietModal/EditDietModal';
import TemplatePickerModal from '../../components/Modals/TemplatePickerModal/TemplatePickerModal';
import DietTypePickerModal from '../../components/Modals/DietTypePickerModal/DietTypePickerModal';
import DietTable from '../../components/DietTable/DietTable';
import AlternativesDietTable from '../../components/AlternativesDietTable/AlternativesDietTable';
import ExamPreview from '../../components/ExamPreview/ExamPreview';
import { muiSelectSx, muiMenuItemSx, sanitizeUrl } from '../../utils/functions';
import AuthenticatedImage from '../../components/AuthenticatedImage/AuthenticatedImage';
import { useDietGeneration } from '../../context/DietGenerationContext';
import { useNotification } from '../../components/notifications/useNotification';
import './ClientDetails.css';
import TourButton from '../../components/TourButton/TourButton';
import { getClientDetailsSteps } from '../../tours/clientDetails';

/* ── Chart configuration ─────────────────────────────────────────────── */
const CHART_TYPES = ['weight', 'upper', 'arms', 'thighs', 'bmi'];

/** Shared props applied to every LineChart */
const CHART_COMMON = {
    grid: { horizontal: true },
    height: 450,
    margin: { left: 70, right: 70 },
};

const ClientDetails = () => {
    const { t } = useTranslation();
    const tourSteps = useMemo(() => getClientDetailsSteps(t), [t]);
    const { upsertGeneration, removeGeneration, generation } = useDietGeneration();
    const { showSuccess, showError } = useNotification();
    const { clientID } = useParams();

    /* ── Client state ────────────────────────────────────────────── */
    const [clientData, setClientData] = useState({});
    const [loading, setLoading] = useState(true);
    const [showEditClientModal, setShowEditClientModal] = useState(false);

    /* ── Measurements / modals ───────────────────────────────────── */
    const [showAddMeasureModal, setShowAddMeasureModal] = useState(false);
    const [showEditMeasureModal, setShowEditMeasureModal] = useState(false);
    const [selectedMeasure, setSelectedMeasure] = useState({});

    /* ── Exams ───────────────────────────────────────────────────── */
    const [showAddExamModal, setShowAddExamModal] = useState(false);
    const [clientExams, setClientExams] = useState([]);
    const [clientTotalExams, setClientTotalExams] = useState(0);
    const [selectedExamIndex, setSelectedExamIndex] = useState(0);

    /* ── Diets ───────────────────────────────────────────────────── */
    const [showGenerateDietModal, setShowGenerateDietModal] = useState(false);
    const [clientDiets, setClientDiets] = useState([]);
    const [clientTotalDiets, setClientTotalDiets] = useState(0);
    const [selectedDiet, setSelectedDiet] = useState(0);
    const [dietMenuAnchorEl, setDietMenuAnchorEl] = useState(null);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);
    const [showEmptyDietTypePicker, setShowEmptyDietTypePicker] = useState(false);
    const [showNewDietModal, setShowNewDietModal] = useState(false);
    const [newDietInitialData, setNewDietInitialData] = useState(null);

    /* ── Charts ──────────────────────────────────────────────────── */
    const [chartType, setChartType] = useState(0);
    const [yearFilter, setYearFilter] = useState('');
    const [zoomFilter, setZoomFilter] = useState(false);
    const [chartData, setChartData] = useState([]);
    const months = [t("Jan"), t("Feb"), t("Mar"), t("Apr"), t("May"), t("Jun"), t("Jul"), t("Aug"), t("Sep"), t("Oct"), t("Nov"), t("Dec")];
    
    useEffect(() => {
        const fetchClientData = async () => {
            try {
                const BASE = process.env.REACT_APP_BACKEND_URL;
                const opts = { withCredentials: true };
                const [
                    responseClientData,
                    responseChartData,
                    responseDietsData,
                    responseExamsData,
                    responseActiveGenerations,
                ] = await Promise.all([
                    axios.get(`${BASE}/clients/${clientID}`, opts),
                    axios.get(`${BASE}/clients/${clientID}/measurements?type=weight`, opts),
                    axios.get(`${BASE}/clients/${clientID}/diets`, opts),
                    axios.get(`${BASE}/clients/${clientID}/exams`, opts),
                    axios.get(`${BASE}/clients/${clientID}/diets/status`, opts),
                ]);
                const activeGeneration = responseActiveGenerations.data;
                if (activeGeneration) upsertGeneration(activeGeneration);
                else removeGeneration();

                setClientExams(responseExamsData.data.exams);
                setClientTotalExams(responseExamsData.data.totalExams);
                setSelectedExamIndex(0);

                setChartData(responseChartData.data);
                setClientData(responseClientData.data);
                setLoading(false);

                const currentDiets = responseDietsData.data.diets;
                if (activeGeneration) {
                    // if there is an active generation, add it to the diets list as placeholder until the diet is generated and the client diets are fetched again
                    setClientDiets([...currentDiets, activeGeneration]);
                    setClientTotalDiets(responseDietsData.data.dietsCount + 1);
                    setSelectedDiet(responseDietsData.data.diets.length);
                } else {
                    setClientDiets(responseDietsData.data.diets);
                    setClientTotalDiets(responseDietsData.data.dietsCount);
                    setSelectedDiet(responseDietsData.data.diets.length - 1);
                }
            } catch (error) {
                console.error("Error fetching client data:", error);
                setLoading(false);
            }
        };
        fetchClientData();
    }, []);

    /**
     * Subscribe to SSE updates.
     */
    useEffect(() => {
        const fetchActiveGenerationUpdates = () => {
            const es = new EventSource(
                `${process.env.REACT_APP_BACKEND_URL}/sse-events/diets?clientId=${clientID}`,
                { withCredentials: true }
            );

            es.addEventListener('diet-progress', (e) => {
                try {
                    const payload = JSON.parse(e.data);
                    upsertGeneration(payload);
                } catch {
                    console.warn('Received malformed diet-progress SSE event');
                }
            });

            es.addEventListener('diet-error', (e) => {
                showError(t("diet-generation-failed"));
                refreshDietsData();
            });

            es.addEventListener('diet-completed', async (e) => {
                removeGeneration();
                showSuccess(t("diet-generation-completed"));

                // fetch client last diets
                let url = `${process.env.REACT_APP_BACKEND_URL}/clients/${clientID}/diets`;
                const responseDietsData = await axios.get(url, { withCredentials: true });
                setClientDiets(responseDietsData.data.diets);
                setClientTotalDiets(responseDietsData.data.dietsCount);
                setSelectedDiet(responseDietsData.data.diets.length - 1);
            });

            es.onerror = () => es.close();

            return () => es.close();
        }
        return fetchActiveGenerationUpdates();
    }, []);
    
    /*
     * Fetches the client data.
    */
    const getClientData = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/clients/${clientID}`, { withCredentials: true });
            setClientData(response.data);
        } catch (error) {
            console.error("Error fetching client data:", error);
        }
    }

    /*
     * Fetches the client body measurements data to update the charts.
     * This function is passed as callback to the add/edit measurement modal to update the charts when a new measurement is added or an existing measurement is edited.
    */    
    const getMeasurementsData = async () => {
        try {
            await updateChart(chartType, yearFilter);
        } catch (error) {
            console.error("Error fetching client measurements data:", error);
        }
    }

    /*
     * Fetches the client exams data.
    */
    const getExamsData = async () => {
        try {
            const url = `${process.env.REACT_APP_BACKEND_URL}/clients/${clientID}/exams`;
            const responseExamsData = await axios.get(url, { withCredentials: true });
            setClientExams(responseExamsData.data.exams);
            setClientTotalExams(responseExamsData.data.totalExams);
            setSelectedExamIndex(0);
        } catch (error) {
            console.error("Error fetching client exams data:", error);
        }
    }

    /*
     * Fetches fresh diets from the server and re-checks the active generation status,
     * then sets all diet-related state atomically.
     * Used after any diet mutation (delete, edit) to avoid stale-closure issues.
    */
    const refreshDietsData = async () => {
        try {
            const url = `${process.env.REACT_APP_BACKEND_URL}/clients/${clientID}/diets`;
            const responseDietsData = await axios.get(url, { withCredentials: true });
            const freshDiets = responseDietsData.data.diets;
            const dietsCount = responseDietsData.data.dietsCount;

            const genUrl = `${process.env.REACT_APP_BACKEND_URL}/clients/${clientID}/diets/status`;
            const responseActiveGenerations = await axios.get(genUrl, { withCredentials: true });
            const activeGeneration = responseActiveGenerations.data;

            if (activeGeneration) {
                upsertGeneration(activeGeneration);
                setClientDiets([...freshDiets, activeGeneration]);
                setClientTotalDiets(dietsCount + 1);
                setSelectedDiet(freshDiets.length);
            } else {
                removeGeneration();
                setClientDiets(freshDiets);
                setClientTotalDiets(dietsCount);
                setSelectedDiet(freshDiets.length - 1);
            }
        } catch (error) {
            console.error("Error refreshing diets data:", error);
        }
    }

    const handleChartChange = async (type = 0) => {
        setChartType(type);
        await updateChart(type, yearFilter);
    }

    const handleYearFilter = async (e) => {
        const year = e.target.value;
        setYearFilter(year);
        await updateChart(chartType, year)
    }

    const updateChart = async (type = 0, year = null) => {
        try {
            const measureType = CHART_TYPES[type] === 'bmi' ? 'weight' : CHART_TYPES[type];
            const params = { type: measureType };
            if (year) params.yearFilter = year;
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/clients/${clientID}/measurements`,
                { params, withCredentials: true }
            );
            setChartData(response.data);
        } catch (error) {
            console.error("Error fetching client data:", error);
        }
    }

    const handleEditMeasure = (measure) => {
        setSelectedMeasure(measure);
        setShowEditMeasureModal(true);
    }

    const handleDietChange = (index) => {
        setSelectedDiet(index);
    }

    const handleExamChange = (index) => {
        setSelectedExamIndex(index);
    }

    const handleDietMenuClose = () => setDietMenuAnchorEl(null);

    const handleEmptyDiet = () => {
        handleDietMenuClose();
        setShowEmptyDietTypePicker(true);
    };

    const handleEmptyDietByType = (isAlternatives) => {
        setShowEmptyDietTypePicker(false);
        setNewDietInitialData({
            dietPlan: isAlternatives
                ? { day0: {}, day1: {}, day2: {}, day3: {} }
                : { day0: {}, day1: {}, day2: {}, day3: {}, day4: {}, day5: {}, day6: {} },
            client: clientData._id,
            intolerances: [],
            isAlternatives,
        });
        setShowNewDietModal(true);
    };

    const handleFromTemplate = () => {
        handleDietMenuClose();
        setShowTemplatePicker(true);
    };

    const handleTemplateSelected = (template) => {
        setShowTemplatePicker(false);
        setNewDietInitialData({
            dietPlan: template.dietPlan,
            client: clientData._id,
            intolerances: template.intolerances ?? [],
            language: template.language ?? '',
            isAlternatives: Boolean(template.isAlternatives),
        });
        setShowNewDietModal(true);
    };

    /* ── Helpers: min-value calculator for zoom ────────────────────── */
    const calcMin = (values) => {
        const clean = values.filter(v => v != null && isFinite(v));
        return clean.length ? Math.min(...clean) * 0.98 : 0;
    };

    /**
     * Builds x-axis labels from chart data (or months as fallback).
     */
    const getXAxisLabels = () =>
        chartData.length
            ? chartData.map(item => formatDate(getFullDate(item.date), yearFilter === '' ? "dd-mm-yyyy" : "dd-mm"))
            : months;

    /**
     * Renders the LineChart for the given chart type.
     *
     * @param {number} type - index into CHART_TYPES
     */
    const renderChart = (type) => {
        const xAxisLabels = getXAxisLabels();
        const onMark = (_e, d) => handleEditMeasure(chartData[d.dataIndex]);
        const noData = { noDataOverlay: { message: t("errorMessages.no-data-to-display") } };

        switch (CHART_TYPES[type]) {
            case 'weight': {
                const minKg = calcMin(chartData.map(i => i.bodyWeight));
                const minPct = calcMin(chartData.map(i => i.bodyFat)) * 0.92;
                return (
                    <LineChart
                        className="chart"
                        xAxis={[{ scaleType: 'band', data: xAxisLabels }]}
                        yAxis={[
                            { id: 'kilograms', scaleType: 'linear', label: 'kg', labelStyle: { transform: 'translateX(-30px)' }, min: zoomFilter ? minKg : 0 },
                            { id: 'percentage', scaleType: 'linear', label: '%', labelStyle: { transform: 'translateX(20px)' }, min: zoomFilter ? minPct : 0, grid: { color: 'lightgrey', lineWidth: 1 } },
                        ]}
                        series={[
                            { curve: 'linear', connectNulls: true, yAxisId: 'kilograms', data: chartData.map(i => i.bodyWeight), label: t('body-weight') },
                            { curve: 'linear', connectNulls: true, yAxisId: 'percentage', data: chartData.map(i => i.bodyFat), label: `${t('body-fat')} %` },
                        ]}
                        leftAxis="kilograms"
                        rightAxis="percentage"
                        colors={['#eded05', '#e3b210']}
                        slotProps={noData}
                        onMarkClick={onMark}
                        {...CHART_COMMON}
                    />
                );
            }
            case 'upper': {
                const fields = ['waist', 'hips', 'chest', 'shoulders', 'neck'];
                const labels = ['Waist', 'Hips', 'Chest', 'Shoulders', 'Neck'];
                const colors = ['#f5a742', '#f5ce42', '#f5ec42', '#ddf542', '#6ea122'];
                const minVal = calcMin(chartData.flatMap(i => fields.map(f => i[f])));
                return (
                    <LineChart
                        className="chart"
                        xAxis={[{ scaleType: 'band', data: xAxisLabels }]}
                        yAxis={[{ id: 'cm', scaleType: 'linear', label: 'cm', labelStyle: { transform: 'translateX(-30px)' }, min: zoomFilter ? minVal : 0 }]}
                        series={fields.map((f, idx) => ({ curve: 'linear', connectNulls: true, yAxisId: 'cm', data: chartData.map(i => i[f]), label: t(labels[idx]) }))}
                        leftAxis="cm"
                        colors={colors}
                        slotProps={noData}
                        onMarkClick={onMark}
                        {...CHART_COMMON}
                    />
                );
            }
            case 'arms': {
                const minVal = calcMin(chartData.flatMap(i => [i.leftArm, i.rightArm]));
                return (
                    <LineChart
                        className="chart"
                        xAxis={[{ scaleType: 'band', data: xAxisLabels }]}
                        yAxis={[{ id: 'cm', scaleType: 'linear', label: 'cm', labelStyle: { transform: 'translateX(-30px)' }, min: zoomFilter ? minVal : 0 }]}
                        series={[
                            { curve: 'linear', connectNulls: true, yAxisId: 'cm', data: chartData.map(i => i.leftArm), label: t('left-arm') },
                            { curve: 'linear', connectNulls: true, yAxisId: 'cm', data: chartData.map(i => i.rightArm), label: t('right-arm') },
                        ]}
                        leftAxis="cm"
                        colors={['#eded05', '#e3b210']}
                        slotProps={noData}
                        onMarkClick={onMark}
                        {...CHART_COMMON}
                    />
                );
            }
            case 'thighs': {
                const minVal = calcMin(chartData.flatMap(i => [i.leftThigh, i.rightThigh]));
                return (
                    <LineChart
                        className="chart"
                        xAxis={[{ scaleType: 'band', data: xAxisLabels }]}
                        yAxis={[{ id: 'cm', scaleType: 'linear', label: 'cm', labelStyle: { transform: 'translateX(-30px)' }, min: zoomFilter ? minVal : 0 }]}
                        series={[
                            { curve: 'linear', connectNulls: true, yAxisId: 'cm', data: chartData.map(i => i.leftThigh), label: t('left-thigh') },
                            { curve: 'linear', connectNulls: true, yAxisId: 'cm', data: chartData.map(i => i.rightThigh), label: t('right-thigh') },
                        ]}
                        leftAxis="cm"
                        colors={['#eded05', '#e3b210']}
                        slotProps={noData}
                        onMarkClick={onMark}
                        {...CHART_COMMON}
                    />
                );
            }
            case 'bmi': {
                const bmiData = chartData.map(i => i.bmi != null && !isNaN(i.bmi) ? parseFloat(i.bmi.toFixed(1)) : null);
                const validBmi = bmiData.filter(v => v !== null);
                const minBmi = validBmi.length ? Math.min(...validBmi) * 0.98 : 0;
                const latestBmi = validBmi.at(-1) ?? null;

                const getBmiCategory = (bmi) => {
                    if (!bmi) return null;
                    if (bmi < 18.5) return { key: 'bmi-underweight', color: '#64b5f6' };
                    if (bmi < 25)   return { key: 'bmi-normal',      color: '#66bb6a' };
                    if (bmi < 30)   return { key: 'bmi-overweight',  color: '#ffa726' };
                    return                  { key: 'bmi-obese',       color: '#ef5350' };
                };
                const bmiCategory = getBmiCategory(latestBmi);

                const bmiSegments = [
                    { cls: 'bmi-underweight', label: '<18.5', key: 'bmi-underweight' },
                    { cls: 'bmi-normal',      label: '18.5–24.9', key: 'bmi-normal' },
                    { cls: 'bmi-overweight',  label: '25–29.9', key: 'bmi-overweight' },
                    { cls: 'bmi-obese',       label: '≥30', key: 'bmi-obese' },
                ];

                return (
                    <>
                        <LineChart
                            className="chart"
                            xAxis={[{ scaleType: 'band', data: xAxisLabels }]}
                            yAxis={[{ id: 'bmi', scaleType: 'linear', label: 'BMI', labelStyle: { transform: 'translateX(-30px)' }, min: zoomFilter ? minBmi : 0 }]}
                            series={[{ curve: 'linear', connectNulls: true, yAxisId: 'bmi', data: bmiData, label: t('BMI') }]}
                            leftAxis="bmi"
                            colors={['#fd4701']}
                            slotProps={noData}
                            onMarkClick={onMark}
                            {...CHART_COMMON}
                        />
                        {latestBmi && bmiCategory && (
                            <div className="cd-bmi-indicator">
                                <div className="cd-bmi-value-wrapper">
                                    <span className="cd-bmi-value">{latestBmi}</span>
                                    <span className="cd-bmi-label">{t('current-bmi')}</span>
                                </div>
                                <div className="cd-bmi-category-badge" style={{ backgroundColor: bmiCategory.color }}>
                                    {t(bmiCategory.key)}
                                </div>
                                <div className="cd-bmi-scale">
                                    {bmiSegments.map(seg => (
                                        <div key={seg.key} className={`cd-bmi-scale-segment ${seg.cls}${bmiCategory.key === seg.key ? ' active' : ''}`}>
                                            <span>{seg.label}</span>
                                            <span>{t(seg.key)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                );
            }
            default:
                return null;
        }
    };

    /* ═══════════════════════════════════════════════════════════════════
       JSX
       ═══════════════════════════════════════════════════════════════════ */
    return (
        <div className="cd-page">
            <div className="cd-page-header">
                <TourButton steps={tourSteps} label={t('tour.tutorial-btn')} />
            </div>
            {/* ─── Profile Card ───────────────────────────────────────── */}
            <div className="cd-profile-card" data-tour="cd-profile">
                {loading
                    ? <span className="cd-skeleton cd-skeleton--avatar" />
                    : <AuthenticatedImage
                        src={sanitizeUrl(clientData.profilePhotoUrl)}
                        fallbackSrc='/images/anonymous_avatar_icon.jpg'
                        alt={`${clientData.name ?? ''} ${clientData.surname ?? ''}`}
                        className="cd-avatar"
                    />
                }
                <div className="cd-info">
                    <div className="cd-name-row">
                        {loading
                            ? <span className="cd-skeleton cd-skeleton--name" />
                            : <>
                                <h2 className="cd-name">{clientData.name} {clientData.surname}</h2>
                                <Tooltip title={t('edit')}>
                                    <div className="cd-edit-icon" onClick={() => setShowEditClientModal(true)}>
                                        <EditOutlinedIcon fontSize="small" />
                                    </div>
                                </Tooltip>
                            </>
                        }
                    </div>
                    <div className="cd-details">
                        {loading ? (
                            <div className="cd-skeleton-details">
                                <span className="cd-skeleton cd-skeleton--text" />
                                <span className="cd-skeleton cd-skeleton--text" />
                                <span className="cd-skeleton cd-skeleton--text" />
                                <span className="cd-skeleton cd-skeleton--text" />
                            </div>
                        ) : (
                            <>
                                <div>
                                    <p><b>{t('Birthdate')}:</b> {formatDate(getFullDate(clientData.birthDate), 'dd-mm-yyyy')}</p>
                                    <p><b>{t('Job')}:</b> {clientData.job}</p>
                                </div>
                                <div>
                                    <p><b>{t('Email')}:</b> {clientData.email}</p>
                                    <p><b>{t('Phone')}:</b> {clientData.phone}</p>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="cd-actions" data-tour="cd-actions">
                    <button className="cd-action-btn" onClick={() => setShowAddMeasureModal(true)}>
                        {t('new-measurement')}
                    </button>
                    <button className="cd-action-btn" onClick={() => setShowAddExamModal(true)}>
                        {capitalize(t('add-exam'))}
                    </button>
                    <button
                        className="cd-action-btn"
                        onClick={(e) => setDietMenuAnchorEl(e.currentTarget)}
                        disabled={generation !== null}
                    >
                        {t('generate-diet')}
                    </button>
                    <Menu
                        anchorEl={dietMenuAnchorEl}
                        open={Boolean(dietMenuAnchorEl)}
                        onClose={handleDietMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <MenuItem onClick={handleEmptyDiet}>
                            <PostAddIcon sx={{ mr: 1.5, fontSize: 20, color: 'var(--primary-color)' }} />
                            {t('empty-diet')}
                        </MenuItem>
                        <MenuItem onClick={handleFromTemplate}>
                            <FileCopyOutlinedIcon sx={{ mr: 1.5, fontSize: 20, color: 'var(--primary-color)' }} />
                            {t('from-template')}
                        </MenuItem>
                        <MenuItem onClick={() => { handleDietMenuClose(); setShowGenerateDietModal(true); }}>
                            <AutoAwesomeIcon sx={{ mr: 1.5, fontSize: 20, color: 'var(--primary-color)' }} />
                            {t('generate-with-ai')}
                        </MenuItem>
                    </Menu>
                </div>
            </div>

            {/* ─── Diet Section ───────────────────────────────────────── */}
            {(clientDiets.length > 0 || generation) && (
                <div className="cd-section-card" data-tour="cd-diet">
                    <h3 className="cd-section-title">{t('Diet')}</h3>
                    <div className="cd-selector">
                        <button className="cd-nav-btn" disabled={selectedDiet === 0} onClick={() => handleDietChange(selectedDiet - 1)}>
                            <ChevronLeftIcon />
                        </button>
                        <ButtonGroup variant="outlined">
                            {clientDiets.map((_, i) => (
                                <Button
                                    key={`diet-${i}`}
                                    onClick={() => handleDietChange(i)}
                                    className={`cd-switch-btn${selectedDiet === i ? ' cd-switch-btn--active' : ''}`}
                                >
                                    {clientTotalDiets - clientDiets.length + i + 1}
                                </Button>
                            ))}
                        </ButtonGroup>
                        <button className="cd-nav-btn" disabled={selectedDiet === clientDiets.length - 1} onClick={() => handleDietChange(selectedDiet + 1)}>
                            <ChevronRightIcon />
                        </button>
                    </div>
                    {clientDiets[selectedDiet]?.isAlternatives ? (
                        <AlternativesDietTable
                            diet={clientDiets[selectedDiet]}
                            refreshDietTable={refreshDietsData}
                            onDeleteDiet={refreshDietsData}
                            client={clientData}
                            dietGeneration={selectedDiet === clientDiets.length - 1 ? generation : null}
                        />
                    ) : (
                        <DietTable
                            diet={clientDiets[selectedDiet]}
                            refreshDietTable={refreshDietsData}
                            onDeleteDiet={refreshDietsData}
                            client={clientData}
                            dietGeneration={selectedDiet === clientDiets.length - 1 ? generation : null}
                        />
                    )}
                </div>
            )}

            {/* ─── Exams Section ──────────────────────────────────────── */}
            {clientExams.length > 0 && (
                <div className="cd-section-card" data-tour="cd-exams">
                    <h3 className="cd-section-title">{capitalize(t('exams'))}</h3>
                    <div className="cd-selector">
                        <button className="cd-nav-btn" disabled={selectedExamIndex === 0} onClick={() => handleExamChange(selectedExamIndex - 1)}>
                            <ChevronLeftIcon />
                        </button>
                        <ButtonGroup variant="outlined">
                            {clientExams.map((_, i) => (
                                <Button
                                    key={`exam-${i}`}
                                    onClick={() => handleExamChange(i)}
                                    className={`cd-switch-btn${selectedExamIndex === i ? ' cd-switch-btn--active' : ''}`}
                                >
                                    {clientTotalExams - clientExams.length + i + 1}
                                </Button>
                            ))}
                        </ButtonGroup>
                        <button className="cd-nav-btn" disabled={selectedExamIndex === clientExams.length - 1} onClick={() => handleExamChange(selectedExamIndex + 1)}>
                            <ChevronRightIcon />
                        </button>
                    </div>
                    <ExamPreview exam={clientExams[selectedExamIndex]} onDelete={() => getExamsData()} />
                </div>
            )}

            {/* ─── Charts Section ─────────────────────────────────────── */}
            <div className="cd-section-card" data-tour="cd-charts">
                <h3 className="cd-section-title">{t('Charts')}</h3>

                {/* Chart type switcher */}
                <div className="cd-chart-switcher">
                    <button className="cd-nav-btn" disabled={chartType === 0} onClick={() => handleChartChange(chartType - 1)}>
                        <ChevronLeftIcon />
                    </button>
                    <ButtonGroup variant="outlined">
                        {CHART_TYPES.map((ct, i) => (
                            <Button
                                key={ct}
                                onClick={() => handleChartChange(i)}
                                className={`cd-switch-btn${chartType === i ? ' cd-switch-btn--active' : ''}`}
                            >
                                {ct === 'bmi' ? 'BMI' : t(capitalize(ct))}
                            </Button>
                        ))}
                    </ButtonGroup>
                    <button className="cd-nav-btn" disabled={chartType === CHART_TYPES.length - 1} onClick={() => handleChartChange(chartType + 1)}>
                        <ChevronRightIcon />
                    </button>
                </div>

                {/* Filters row */}
                <div className="cd-chart-filters">
                    <FormControl size="small" sx={{ width: 150 }}>
                        <MuiSelect
                            value={yearFilter}
                            onChange={handleYearFilter}
                            displayEmpty
                            renderValue={(v) => v || t('Year')}
                            sx={muiSelectSx}
                            endAdornment={
                                yearFilter ? (
                                    <IconButton size="small" onClick={() => handleYearFilter({ target: { value: '' } })} sx={{ mr: 1 }}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                ) : null
                            }
                        >
                            {Array.from({ length: new Date().getFullYear() - 2021 }, (_, i) => {
                                const yr = (new Date().getFullYear() - i).toString();
                                return <MenuItem key={yr} value={yr} sx={muiMenuItemSx}>{yr}</MenuItem>;
                            })}
                        </MuiSelect>
                    </FormControl>
                    <Tooltip title={t('zoom-the-chart')}>
                        <Fab
                            size="small"
                            className={`cd-zoom-btn${zoomFilter ? ' cd-zoom-btn--active' : ''}`}
                            onClick={() => setZoomFilter(!zoomFilter)}
                        >
                            <SearchIcon fontSize="small" />
                        </Fab>
                    </Tooltip>
                </div>

                {/* Chart */}
                <div className="cd-chart-area">
                    {renderChart(chartType)}
                </div>
            </div>

            {/* ─── Modals ─────────────────────────────────────────────── */}
            {showEditClientModal && (
                <AddClientModal client={clientData} setShowModal={setShowEditClientModal} onSave={() => getClientData()} />
            )}
            {showAddMeasureModal && (
                <AddMeasureModal setShowModal={setShowAddMeasureModal} client={clientData} onSubmit={() => getMeasurementsData()} />
            )}
            {showAddExamModal && (
                <AddExamModal setShowModal={setShowAddExamModal} client={clientData} onSubmit={() => getExamsData()} />
            )}
            {showEditMeasureModal && (
                <AddMeasureModal setShowModal={setShowEditMeasureModal} client={clientData} onSubmit={() => getMeasurementsData()} onDelete={() => getMeasurementsData()} data={selectedMeasure} />
            )}
            {showGenerateDietModal && (
                <GenerateDietModal setShowModal={setShowGenerateDietModal} client={clientData} onSubmit={refreshDietsData} />
            )}
            {showTemplatePicker && (
                <TemplatePickerModal setShowModal={setShowTemplatePicker} onSelect={handleTemplateSelected} />
            )}
            {showEmptyDietTypePicker && (
                <DietTypePickerModal
                    title={t('empty-diet')}
                    onWeekly={() => handleEmptyDietByType(false)}
                    onAlternatives={() => handleEmptyDietByType(true)}
                    onClose={() => setShowEmptyDietTypePicker(false)}
                />
            )}
            {showNewDietModal && newDietInitialData && (
                <EditDietModal setShowModal={setShowNewDietModal} diet={newDietInitialData} onSave={refreshDietsData} isNew={true} />
            )}
        </div>
    );
};

export default ClientDetails;
